import React, { useEffect, useRef, useState } from 'react';
import { defineMessages, MessageDescriptor, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useDataset } from '../../../../util/api';
import {
  MyOmeRerunResponse,
  useGenomicIngestionMutation,
} from '../../../../util/genomicIngestionApi';
import {
  getMyOmeRequisitionCopy,
  getPlacedMyOmeStatus,
  isPlacedMyOmeRequisition,
  MyOmeRequisitionCopy,
} from './myOmeRequisitionState';
import type { MyOmeRequisition } from './useMyOmeRequisition';

// Draft copy for Josh (FLENG-176 decision 13).
const messages = defineMessages({
  title: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.title',
    defaultMessage: 'MyOme requisition details',
  },
  iprsTitle: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.iprsTitle',
    defaultMessage: 'MyOme iPRS requisition details',
  },
  missing: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.missing',
    defaultMessage: 'Missing:',
  },
  myomeStatusChangedAt: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.myomeStatusChangedAt',
    defaultMessage: 'MyOme status last changed {changedAt}.',
  },
  myomeStatusPending: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.myomeStatusPending',
    defaultMessage:
      "MyOme hasn't reported a status yet. It's checked every 15 minutes.",
  },
  elationSync: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.elationSync',
    defaultMessage:
      'Fixed something in Elation? It can take a few minutes to reach PHC. Re-run once it has.',
  },
  contactSupport: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.contactSupport',
    defaultMessage:
      'Need help? Contact support in #tech-support and include the reference below.',
  },
  reference: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.reference',
    defaultMessage: 'Elation lab order {elationLabOrderId}',
  },
  close: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.close',
    defaultMessage: 'Close',
  },
  print: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.print',
    defaultMessage: 'Print',
  },
  rerun: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.rerun',
    defaultMessage: 'Re-run',
  },
  rerunning: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.rerunning',
    defaultMessage: 'Re-running…',
  },
  placed: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.placed',
    defaultMessage: 'Placed with MyOme. RQ id {rqId}.',
  },
  placedWithoutRqId: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.placedWithoutRqId',
    defaultMessage: 'Placed with MyOme.',
  },
  stillWaiting: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.stillWaiting',
    defaultMessage: 'Re-run finished. Still waiting: {explanation}',
  },
  failed: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.failed',
    defaultMessage: 'Re-run finished. The order failed: {explanation}',
  },
  finishedWithLabel: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.finishedWithLabel',
    defaultMessage: 'Re-run finished. {label}.',
  },
  noOpInFlight: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.noOpInFlight',
    defaultMessage: 'This order is already being placed with MyOme.',
  },
  noOpDone: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.noOpDone',
    defaultMessage: 'This order is already placed with MyOme.',
  },
  noOpNotRerunnable: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.noOpNotRerunnable',
    defaultMessage: "This order can't be re-run. Contact support.",
  },
  noOpDebounced: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.noOpDebounced',
    defaultMessage:
      'This order was re-run moments ago. Wait 30 seconds, then try again.',
  },
  noOpChanged: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.noOpChanged',
    defaultMessage:
      'The order changed while re-running. It now shows its latest status.',
  },
  forbidden: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.forbidden',
    defaultMessage: "You don't have permission to re-run MyOme orders.",
  },
  unavailable: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.unavailable',
    defaultMessage: "Re-run isn't available yet. Contact support.",
  },
  rerunError: {
    id: 'MemberDetail.myOmeRequisitionDetailsModal.rerunError',
    defaultMessage: "Couldn't re-run the order. Try again.",
  },
});

const NO_OP_MESSAGES: Record<
  NonNullable<MyOmeRerunResponse['noOpReason']>,
  MessageDescriptor
> = {
  'in-flight': messages.noOpInFlight,
  done: messages.noOpDone,
  'not-rerunnable': messages.noOpNotRerunnable,
  debounced: messages.noOpDebounced,
};

const httpStatusOf = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status;

// GIS writes the Task from its table's stream after it answers, so the first
// refetch can still find the old one.
const REFETCH_DELAYS_MS = [0, 2000, 5000, 10000];

/**
 * Re-runs one requisition and describes GIS's answer. The tile owns it, so
 * closing the modal mid-request doesn't allow a second press.
 */
export const useMyOmeRerun = (
  requisition: MyOmeRequisition,
  {
    onAnswered,
    refetch,
  }: {
    onAnswered: (response: MyOmeRerunResponse) => void;
    refetch: () => void;
  },
) => {
  const { formatMessage } = useIntl();
  const dataset = useDataset();
  const [outcome, setOutcome] = useState<string>();
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // react-query still runs mutation callbacks after the tile unmounts.
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
      timers.current.forEach(clearTimeout);
    },
    [],
  );

  const describeOutcome = (response: MyOmeRerunResponse): string => {
    switch (response.outcome) {
      case 'placed':
        return response.rqId
          ? formatMessage(messages.placed, { rqId: response.rqId })
          : formatMessage(messages.placedWithoutRqId);
      case 'waiting':
      case 'failed': {
        const { label, explanation } = getMyOmeRequisitionCopy({
          businessStatus: response.status,
          reasonCode: response.reasonCode,
          failureClass: response.failureClass,
          missing: response.missing ?? [],
          collection: requisition.collection,
        });
        if (!explanation) {
          return formatMessage(messages.finishedWithLabel, { label });
        }
        return formatMessage(
          response.outcome === 'waiting'
            ? messages.stillWaiting
            : messages.failed,
          { explanation },
        );
      }
      case 'no-op':
        return formatMessage(
          response.noOpReason
            ? NO_OP_MESSAGES[response.noOpReason]
            : messages.noOpChanged,
        );
    }
  };

  const mutation = useGenomicIngestionMutation(
    'POST /v1/genomic-ingestion/projects/:projectId/myome/requisitions/:requisitionId/rerun',
    {
      onSuccess: response => {
        setOutcome(describeOutcome(response));
        onAnswered(response);
        timers.current.forEach(clearTimeout);
        if (!mounted.current) {
          return;
        }
        timers.current = REFETCH_DELAYS_MS.map(delay =>
          setTimeout(refetch, delay),
        );
      },
      onError: error => {
        const status = httpStatusOf(error);
        setOutcome(
          formatMessage(
            status === 403
              ? messages.forbidden
              : status === 404
              ? messages.unavailable
              : messages.rerunError,
          ),
        );
      },
    },
  );

  const { requisitionId } = requisition;
  return {
    outcome,
    isRerunning: mutation.isLoading,
    rerun: requisitionId
      ? () => {
          setOutcome(undefined);
          mutation.mutate({ projectId: dataset.id, requisitionId });
        }
      : undefined,
  };
};

export type MyOmeRequisitionDetailsModalProps = {
  requisition: MyOmeRequisition;
  copy: MyOmeRequisitionCopy;
  /** Set only for a placed order with a kit sheet to print. */
  print?: () => void;
  /** Set only when GIS would act and the user may write. */
  rerun?: () => void;
  isRerunning: boolean;
  outcome?: string;
  onClose: () => void;
};

/** When a placed order's MyOme status, its copy's label, last changed. */
const useMyOmeStatusLine = (requisition: MyOmeRequisition) => {
  const { formatMessage, formatDate } = useIntl();
  if (!isPlacedMyOmeRequisition(requisition)) {
    return undefined;
  }
  if (!getPlacedMyOmeStatus(requisition)) {
    return formatMessage(messages.myomeStatusPending);
  }
  // react-intl 3 drops `dateStyle` and `timeStyle`.
  return requisition.myomeStatusChangedAt
    ? formatMessage(messages.myomeStatusChangedAt, {
        changedAt: formatDate(requisition.myomeStatusChangedAt, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }),
      })
    : undefined;
};

export const MyOmeRequisitionDetailsModal = ({
  requisition,
  copy,
  print,
  rerun,
  isRerunning,
  outcome,
  onClose,
}: MyOmeRequisitionDetailsModalProps) => {
  const { formatMessage } = useIntl();
  const statusLine = useMyOmeStatusLine(requisition);

  return (
    <Modal
      isOpen
      size={1}
      title={formatMessage(
        requisition.kind === 'iprs' ? messages.iprsTitle : messages.title,
      )}
      onDismiss={onClose}
      actions={
        <>
          <Button variant="text" onClick={onClose}>
            {formatMessage(messages.close)}
          </Button>
          {print ? (
            <Button onClick={print}>{formatMessage(messages.print)}</Button>
          ) : null}
          {rerun ? (
            <Button disabled={isRerunning} onClick={rerun}>
              {formatMessage(isRerunning ? messages.rerunning : messages.rerun)}
            </Button>
          ) : null}
        </>
      }
    >
      <Box direction="column" gap={1}>
        <Text size="subbody" weight="bold">
          {copy.label}
        </Text>
        {copy.explanation ? (
          <Text size="subbody">{copy.explanation}</Text>
        ) : null}
        {statusLine ? <Text size="subbody">{statusLine}</Text> : null}
        {requisition.missing.length > 0 ? (
          <div>
            <Text size="subbody">{formatMessage(messages.missing)}</Text>
            <ul>
              {requisition.missing.map(({ name, source }) => (
                <li key={`${source}:${name}`}>
                  <Text size="subbody">{name}</Text>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {copy.nextStep ? <Text size="subbody">{copy.nextStep}</Text> : null}
        {rerun ? (
          <Text size="caption">{formatMessage(messages.elationSync)}</Text>
        ) : null}
        {outcome ? (
          <div role="status">
            <Text size="subbody" weight="bold">
              {outcome}
            </Text>
          </div>
        ) : null}
        <Text size="subbody">{formatMessage(messages.contactSupport)}</Text>
        <Text size="subbody" weight="bold">
          {formatMessage(messages.reference, {
            elationLabOrderId: requisition.elationLabOrderId ?? '—',
          })}
        </Text>
      </Box>
    </Modal>
  );
};
